import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import useSettings from 'store/settings';
import useWebsiteMetas from 'store/metas';
import useSelectedLanguage from 'store/language';
import useTranslates from 'utils/translate';
import { PAGE_META_KEYS, DEFAULT_META_KEY, APPLICATION_NAME } from 'config';
import useGlobalSettings from 'store/globalSettings';
import useSelectedTheme from 'store/theme';

const Metatags = () => {
  const location = useLocation();
  const { translate } = useTranslates();
  const [selectedLanguage] = useSelectedLanguage();
  const [settings] = useSettings();
  const [state, actions] = useWebsiteMetas();
  const [globalSettings] = useGlobalSettings();
  const [configs] = useSettings();
  const [{ isDark }] = useSelectedTheme();

  const selected = state?.data?.find(
      (item) => item.page === location?.pathname || PAGE_META_KEYS[location?.pathname] === item.page
  );

  const defaultSelected = state?.data?.find((item) => item.page === DEFAULT_META_KEY);

  const metaTitle = selected?.metaTitle || defaultSelected?.metaTitle || APPLICATION_NAME;
  const metaDescription = selected?.metaDescription || defaultSelected?.metaDescription;
  const favicon = settings.data?.favicon;
  const logoImage = configs.data?.[isDark ? 'logoDark' : 'logo'];

  useEffect(() => {
    if (selectedLanguage?.id) {
      actions.read();
    }
  }, [selectedLanguage?.id]);

  // TikTok Script
  useEffect(() => {
    if(globalSettings?.data?.meta?.tiktokScript) {
      const script = document.createElement("script");
      script.innerHTML = `
        !function (w, d, t) {
          w.TiktokAnalyticsObject=t;
          var ttq=w[t]=w[t]||[];
          ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
          ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
          for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
          ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
          ttq.load=function(e,n){
            var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;
            ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,
            ttq._o=ttq._o||{},ttq._o[e]=n||{};
            n=document.createElement("script");
            n.type="text/javascript";n.async=!0;n.src=r+"?sdkid="+e+"&lib="+t;
            e=document.getElementsByTagName("script")[0];
            e.parentNode.insertBefore(n,e)
          };
          ttq.load('${globalSettings?.data?.meta?.tiktokScript}');
          ttq.page();
        }(window, document, 'ttq');
      `;
      document.head.appendChild(script);
    }
  }, [globalSettings?.data?.meta?.tiktokScript]);

  // Google Tag Manager Script
  useEffect(() => {
    if(globalSettings?.data?.meta?.googleTagManager) {
      const gtmID = globalSettings.data.meta.googleTagManager;
      const script = document.createElement('script');
      script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmID}');
    `;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }, [globalSettings?.data?.meta?.googleTagManager]);

  useEffect(() => {
    const cleanDescription = metaDescription?.replace(/<\/?[^>]+(>|$)/g, "") || "";

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Brand",
      "name": metaTitle,
      "url": `${window.location.origin}${location.pathname}`,
      "logo": logoImage,
      "image": logoImage,
      "description": cleanDescription,
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.insertBefore(script, document.head.firstChild);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [metaTitle, metaDescription, favicon, location.pathname, isDark]);


  // Google Analytics Script
  useEffect(() => {

    if (globalSettings?.data?.meta?.googleAnalytics) {
      const gaID = globalSettings.data.meta.googleAnalytics;

      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaID}`;
      document.head.appendChild(script);

      const inlineScript = document.createElement("script");
      inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaID}');
    `;
      document.head.appendChild(inlineScript);

      return () => {
        if (script.parentNode) script.parentNode.removeChild(script);
        if (inlineScript.parentNode) inlineScript.parentNode.removeChild(inlineScript);
      };
    }
  }, [globalSettings?.data?.meta?.googleAnalytics]);

  useEffect(() => {
    const meta = globalSettings?.data?.meta;
    const hooryKey = meta?.hooryKey;
    const liveChatKey = meta?.liveChatKey;
    const BASE_URL = "https://app.hoory.com";

    const existingHoory = document.querySelector('script[data-hoory="true"]');

    if (!hooryKey || liveChatKey) {
      if (existingHoory) existingHoory.remove();
      return;
    }

    if (!existingHoory) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.dataset.hoory = "true";
      script.innerHTML = `
      (function(d, t) {
        var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
        g.src = '${BASE_URL}/packs/js/sdk.js';
        g.defer = true;
        g.async = true;
        s.parentNode.insertBefore(g, s);
        g.onload = function() {
          window.hoorySDK.run({
            websiteToken: '${hooryKey}',
            baseUrl: '${BASE_URL}'
          });
        };
      })(document, "script");
    `;
      document.head.insertBefore(script, document.head.firstChild);
    }

    return () => {
      const script = document.querySelector('script[data-hoory="true"]');
      if (script) script.remove();
    };
  }, [globalSettings?.data?.meta?.hooryKey, globalSettings?.data?.meta?.liveChatKey]);


  return (
      <>
        <Helmet>
          <meta charSet="utf-8" />
          <link rel="apple-touch-icon" href={favicon} />
          <link rel="icon" href={favicon} type="image/x-icon" />
          <link rel="shortcut icon" href={favicon} type="image/x-icon" />
          <title>
            {state?.status === 'loading'
                ? translate('meta_loading')
                : selected?.metaTitle || defaultSelected?.metaTitle || APPLICATION_NAME}
          </title>
          <meta name="application-name" content={selected?.metaTitle || defaultSelected?.metaTitle || APPLICATION_NAME} />
          <meta name="keywords" content={selected?.metaKeywords || defaultSelected?.metaKeywords} />
          <meta name="description" content={selected?.metaDescription || defaultSelected?.metaDescription} />
        </Helmet>
      </>
  );
};

export default Metatags;
